// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import matter from "gray-matter"
import * as Log from "@opencode-ai/core/util/log"
import { Instance } from "@/project/instance"
import { SkillState, type SkillScope } from "@/infcode/skill/skill.sql"
import { SKILL_NAME_REGEX, isValidSkillName } from "@/infcode/skill/shared"
import { discoverProjectDirectories, type ProjectDiscoveryIssue } from "./discovery"
import { addMigrationItem, createMigrationReport, type MigrationReport } from "./types"
import { errorMessage, exists, isRecord } from "./util"

const LOG_PREFIX = "[infcode:migration:skills]"
const log = Log.create({ service: LOG_PREFIX })

const SKILL_FILE = "SKILL.md"
const SKILL_JSON = "skill.json"

export type SkillsMigrationItem = {
  status: "migrated" | "rewritten" | "skipped" | "failed"
  scope: SkillScope
  sourcePath: string
  targetPath?: string
  backupPath?: string
  projectDir?: string
  projectId?: string
  legacyName?: string
  finalName?: string
  enabled?: boolean
  normalized?: boolean
  reason?: string
  message?: string
}

export type SkillsMigrationReport = MigrationReport<SkillsMigrationItem>

type SkillMeta = {
  name: string
  enabled?: boolean
  folderPath?: string
}

type ParsedSkill = {
  data: Record<string, unknown>
  content: string
}

type SkillCandidate = {
  dir: string
  skillFile: string
  dirName: string
}

type SkillsDirectoryInput = {
  report: SkillsMigrationReport
  scope: SkillScope
  skillsDir: string
  backupDir: string
  projectDir?: string
  projectId?: string
}

export namespace SkillsMigration {
  export type Item = SkillsMigrationItem
  export type Report = SkillsMigrationReport

  export async function run(input: { home: string }): Promise<SkillsMigrationReport> {
    const report = createMigrationReport<SkillsMigrationItem>()
    await migrateSkillsDirectorySafely({
      report,
      scope: "global",
      skillsDir: path.join(input.home, ".infcode", "skills"),
      backupDir: path.join(input.home, ".infcode", "migration", "v1-backup", "skills", "global"),
      projectId: "",
    })

    const { projects, issues } = await discoverProjectDirectories(input.home)
    addDiscoveryIssues(report, issues)

    for (const projectDir of projects) {
      await migrateSkillsDirectorySafely({
        report,
        scope: "project",
        skillsDir: path.join(projectDir, ".infcode", "skills"),
        backupDir: path.join(projectDir, ".infcode", "migration", "v1-backup", "skills"),
        projectDir,
        projectId: await resolveProjectId(projectDir),
      })
    }

    return report
  }
}

async function migrateSkillsDirectorySafely(input: SkillsDirectoryInput) {
  try {
    await migrateSkillsDirectory(input)
  } catch (err) {
    const message = errorMessage(err)
    log.warn("failed to migrate v1 skills directory", {
      scope: input.scope,
      skillsDir: input.skillsDir,
      projectDir: input.projectDir,
      error: message,
    })
    addMigrationItem(input.report, {
      status: "failed",
      scope: input.scope,
      sourcePath: input.skillsDir,
      targetPath: input.skillsDir,
      backupPath: input.backupDir,
      projectDir: input.projectDir,
      projectId: input.projectId,
      reason: "skills-directory-failed",
      message,
    })
  }
}

function addDiscoveryIssues(report: SkillsMigrationReport, issues: ProjectDiscoveryIssue[]) {
  for (const issue of issues) {
    if (issue.kind === "sessions-json-invalid") {
      addMigrationItem(report, {
        status: "failed",
        scope: "project",
        sourcePath: issue.sourcePath,
        reason: "sessions-json-invalid",
        message: issue.message,
      })
      continue
    }
    addMigrationItem(report, {
      status: "skipped",
      scope: "project",
      sourcePath: issue.sourcePath,
      targetPath: path.join(issue.workspaceDirectory ?? "", ".infcode", "skills"),
      reason: "workspace-missing",
    })
  }
}

async function migrateSkillsDirectory(input: SkillsDirectoryInput) {
  if (!(await isDirectory(input.skillsDir))) return
  const backupPath = await backupSkillsRoot(input.skillsDir, input.backupDir)
  const metas = await readSkillMetadata({
    report: input.report,
    scope: input.scope,
    skillsDir: input.skillsDir,
    backupPath,
    projectDir: input.projectDir,
    projectId: input.projectId,
  })
  const candidates = await listSkillCandidates(input.skillsDir)
  const reservedNames = await initialReservedNames(candidates)

  for (const candidate of candidates) {
    await migrateSkill({
      ...input,
      backupPath,
      metas,
      reservedNames,
      candidate,
    })
  }
}

async function migrateSkill(input: {
  report: SkillsMigrationReport
  scope: SkillScope
  skillsDir: string
  backupDir: string
  backupPath: string
  projectDir?: string
  projectId?: string
  metas: Map<string, SkillMeta>
  reservedNames: Set<string>
  candidate: SkillCandidate
}) {
  const sourcePath = input.candidate.skillFile
  try {
    const raw = await fs.readFile(sourcePath, "utf8")
    const parsed = parseSkill(raw)
    const legacyName = resolveLegacyName(input.candidate, parsed, input.metas)
    const meta = input.metas.get(legacyName) ?? input.metas.get(input.candidate.dirName)
    const enabled = meta?.enabled
    const normalizedBase = normalizeSkillName(legacyName)
    const needsCopy = !isValidSkillName(legacyName)
    const finalName = needsCopy ? await nextAvailableName(input.skillsDir, normalizedBase, input.reservedNames) : legacyName
    const targetDir = path.join(input.skillsDir, finalName)
    const targetPath = path.join(targetDir, SKILL_FILE)

    if (needsCopy) {
      await copyNormalizedSkill({
        sourceDir: input.candidate.dir,
        targetDir,
        parsed,
        finalName,
      })
    }
    input.reservedNames.add(finalName)

    const status = needsCopy ? "rewritten" : "migrated"
    const stateReason = await migrateEnabledState({
      scope: input.scope,
      projectId: input.projectId,
      finalName,
      enabled,
    })

    addMigrationItem(input.report, {
      status,
      scope: input.scope,
      sourcePath,
      targetPath: needsCopy ? targetPath : sourcePath,
      backupPath: path.join(input.backupPath, path.relative(input.skillsDir, sourcePath)),
      projectDir: input.projectDir,
      projectId: input.projectId,
      legacyName,
      finalName,
      enabled,
      normalized: needsCopy,
      reason: stateReason ?? (needsCopy ? "normalized-skill-name" : undefined),
    })
  } catch (err) {
    const message = errorMessage(err)
    log.warn("failed to migrate v1 skill", { sourcePath, error: message })
    addMigrationItem(input.report, {
      status: "failed",
      scope: input.scope,
      sourcePath,
      targetPath: sourcePath,
      backupPath: path.join(input.backupPath, path.relative(input.skillsDir, sourcePath)),
      projectDir: input.projectDir,
      projectId: input.projectId,
      reason: "migration-failed",
      message,
    })
  }
}

async function migrateEnabledState(input: {
  scope: SkillScope
  projectId?: string
  finalName: string
  enabled?: boolean
}): Promise<string | undefined> {
  if (input.enabled !== false) return undefined
  if (input.scope === "project" && !input.projectId) return "missing-project-id"
  SkillState.setEnabled(input.scope, input.scope === "project" ? input.projectId ?? "" : "", input.finalName, false)
  return undefined
}

async function backupSkillsRoot(sourceDir: string, backupDir: string): Promise<string> {
  if (await exists(backupDir)) return backupDir
  await fs.mkdir(path.dirname(backupDir), { recursive: true })
  await fs.cp(sourceDir, backupDir, { recursive: true, force: false, errorOnExist: false })
  return backupDir
}

async function readSkillMetadata(input: {
  report: SkillsMigrationReport
  scope: SkillScope
  skillsDir: string
  backupPath: string
  projectDir?: string
  projectId?: string
}): Promise<Map<string, SkillMeta>> {
  const file = path.join(input.skillsDir, SKILL_JSON)
  const metas = new Map<string, SkillMeta>()
  if (!(await exists(file))) return metas
  try {
    const parsed = JSON.parse(await fs.readFile(file, "utf8")) as unknown
    if (!Array.isArray(parsed)) throw new Error("skill.json root is not an array")
    for (const item of parsed) {
      if (!isRecord(item) || typeof item.name !== "string" || !item.name.trim()) continue
      const folderPath = typeof item.folderPath === "string" && item.folderPath.trim() ? item.folderPath.trim() : undefined
      const meta = {
        name: item.name.trim(),
        enabled: typeof item.enabled === "boolean" ? item.enabled : undefined,
        folderPath,
      }
      metas.set(meta.name, meta)
      if (folderPath) metas.set(path.basename(folderPath), meta)
    }
  } catch (err) {
    addMigrationItem(input.report, {
      status: "failed",
      scope: input.scope,
      sourcePath: file,
      targetPath: file,
      backupPath: path.join(input.backupPath, SKILL_JSON),
      projectDir: input.projectDir,
      projectId: input.projectId,
      reason: "skill-json-invalid",
      message: errorMessage(err),
    })
  }
  return metas
}

async function listSkillCandidates(root: string): Promise<SkillCandidate[]> {
  const entries = await fs.readdir(root, { withFileTypes: true })
  const candidates: SkillCandidate[] = []
  for (const entry of entries) {
    if (!entry.isDirectory()) continue
    const dir = path.join(root, entry.name)
    const skillFile = path.join(dir, SKILL_FILE)
    if (!(await exists(skillFile))) continue
    candidates.push({ dir, skillFile, dirName: entry.name })
  }
  return candidates.sort((a, b) => a.dirName.localeCompare(b.dirName))
}

async function initialReservedNames(candidates: SkillCandidate[]): Promise<Set<string>> {
  const reserved = new Set<string>()
  for (const candidate of candidates) {
    if (isValidSkillName(candidate.dirName)) reserved.add(candidate.dirName)
  }
  return reserved
}

function parseSkill(text: string): ParsedSkill {
  const parsed = matter(text) as { data: unknown; content: string }
  return {
    data: isRecord(parsed.data) ? parsed.data : {},
    content: parsed.content,
  }
}

function resolveLegacyName(candidate: SkillCandidate, parsed: ParsedSkill, metas: Map<string, SkillMeta>): string {
  const frontmatterName = typeof parsed.data.name === "string" && parsed.data.name.trim() ? parsed.data.name.trim() : undefined
  if (frontmatterName) return frontmatterName
  const meta = metas.get(candidate.dirName)
  if (meta?.name) return meta.name
  return candidate.dirName
}

function normalizeSkillName(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 64)
    .replace(/-+$/g, "")
  const safe = normalized || "skill"
  return SKILL_NAME_REGEX.test(safe) ? safe : "skill"
}

async function nextAvailableName(root: string, base: string, reservedNames: Set<string>): Promise<string> {
  if (!reservedNames.has(base) && !(await exists(path.join(root, base)))) return base
  for (let index = 2; ; index++) {
    const suffix = `-${index}`
    const stem = base.slice(0, 64 - suffix.length).replace(/-+$/g, "") || "skill"
    const candidate = `${stem}${suffix}`
    if (!reservedNames.has(candidate) && !(await exists(path.join(root, candidate)))) return candidate
  }
}

async function copyNormalizedSkill(input: {
  sourceDir: string
  targetDir: string
  parsed: ParsedSkill
  finalName: string
}) {
  if (!(await exists(input.targetDir))) {
    await fs.mkdir(path.dirname(input.targetDir), { recursive: true })
    await fs.cp(input.sourceDir, input.targetDir, { recursive: true, force: false, errorOnExist: false })
  }
  const next = matter.stringify(input.parsed.content, { ...input.parsed.data, name: input.finalName })
  await fs.writeFile(path.join(input.targetDir, SKILL_FILE), next, "utf8")
}

async function isDirectory(file: string) {
  try {
    return (await fs.stat(file)).isDirectory()
  } catch {
    return false
  }
}

async function resolveProjectId(projectDir: string): Promise<string | undefined> {
  try {
    return await Instance.provide({ directory: projectDir, fn: () => Instance.project.id })
  } catch (err) {
    log.warn("failed to resolve project id for v1 skills migration", { projectDir, error: errorMessage(err) })
    return undefined
  }
}
